import {
  coordinateFormatAbbreviation,
  formatCoordinatePair,
  normalizeCoordinateFormat,
} from "./coordinateFormat"

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/**
 * pdfExport.js
 * Captures the current map view + POI table and opens a print dialog
 * (File > Print > Save as PDF) in a new window.  Zero extra dependencies.
 *
 * @param {object} map          – live mapbox-gl Map instance
 * @param {object} featureCollection – current GeoJSON FeatureCollection
 * @param {string} projectName  – used as the document title
 * @param {string} coordinateFormat – shared coordinate-format catalog key
 */
export async function exportMapPdf(
  map,
  featureCollection,
  projectName,
  coordinateFormat = "ddm",
) {
  const selectedFormat = normalizeCoordinateFormat(coordinateFormat)
  // 1. Capture the map canvas as a PNG data-URL
  const mapDataUrl = map.getCanvas().toDataURL("image/png")

  // 2. Extract Point features for the POI table
  const pois = (featureCollection?.features ?? []).filter(
    (f) => f?.geometry?.type === "Point" || f?.geometry?.type === "MultiPoint"
  )

  function coordsFromFeature(f) {
    const g = f?.geometry
    if (!g) return null
    // For MultiPoint we take the first point
    const c = g.type === "Point" ? g.coordinates : g.coordinates?.[0]
    if (!c || c.length < 2) return null
    return { lng: c[0], lat: c[1] }
  }

  const tableRows = pois.map((f) => {
    const name = f?.properties?.name ?? "—"
    const c = coordsFromFeature(f)
    const coord = c ? formatCoordinatePair(c.lat, c.lng, selectedFormat) : "—"
    return { name, coord }
  })

  // 4. Build the HTML document for the print window
  const safeTitle = escapeHtml(projectName || "magpie-export")
  const now = new Date().toLocaleString()

  const tableHtml =
    tableRows.length > 0
      ? `<table>
          <thead>
            <tr><th>#</th><th>Name</th><th>Coordinates (${escapeHtml(coordinateFormatAbbreviation(selectedFormat))})</th></tr>
          </thead>
          <tbody>
            ${tableRows
              .map(
                (r, i) =>
                  `<tr>
                    <td>${i + 1}</td>
                    <td>${escapeHtml(r.name)}</td>
                    <td class="coord">${escapeHtml(r.coord)}</td>
                  </tr>`
              )
              .join("\n")}
          </tbody>
        </table>`
      : `<p class="no-pois">No point features in this project.</p>`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${safeTitle}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
      margin: 0;
      padding: 24px 32px;
      color: #111;
      background: #fff;
    }

    h1 {
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 4px;
    }

    .meta {
      font-size: 11px;
      color: #666;
      margin: 0 0 16px;
    }

    .map-image {
      width: 100%;
      max-height: 420px;
      object-fit: contain;
      border: 1px solid #ccc;
      border-radius: 4px;
      display: block;
      margin-bottom: 20px;
    }

    h2 {
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 8px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 4px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }

    thead tr {
      background: #1a1a2e;
      color: #fff;
    }

    thead th {
      padding: 6px 8px;
      text-align: left;
      font-weight: 600;
    }

    tbody tr:nth-child(even) { background: #f5f5f5; }

    tbody td {
      padding: 5px 8px;
      border-bottom: 1px solid #e8e8e8;
      vertical-align: top;
    }

    td:first-child {
      color: #888;
      width: 32px;
      text-align: right;
      padding-right: 12px;
    }

    .coord {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
      white-space: nowrap;
    }

    .no-pois {
      font-size: 12px;
      color: #888;
      font-style: italic;
    }

    @media print {
      body { padding: 12px 16px; }
      .map-image { max-height: 380px; }
    }
  </style>
</head>
<body>
  <h1>${safeTitle}</h1>
  <p class="meta">Exported ${now} · ${tableRows.length} point feature${tableRows.length !== 1 ? "s" : ""}</p>

  <img class="map-image" src="${mapDataUrl}" alt="Map view" />

  <h2>Points of Interest</h2>
  ${tableHtml}
</body>
</html>`

  // 5. Open a new window, write the HTML, and trigger print
  const win = window.open("", "_blank", "width=900,height=700")
  if (!win) {
    throw new Error(
      "Could not open print window. Allow pop-ups for this site and try again."
    )
  }
  win.document.open()
  win.document.write(html)
  win.document.close()

  // Wait for the image to fully load before printing
  win.onload = () => win.print()
  // Fallback: if onload already fired (image is data-url so it's instant)
  setTimeout(() => {
    try { win.print() } catch { /* already printed */ }
  }, 400)
}
